/**
 * Customer Messages — WhatsApp-style SMS module.
 *
 * Single-file MVP that handles 3 views via local state:
 *   - conversations list
 *   - new-message composer (full screen)
 *   - per-conversation thread with inline composer
 *
 * Routes:
 *   /app/messages           list
 *   /app/messages/new       composer
 *   /app/messages/:phone    thread (phone is URL-encoded E.164)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Send, Search, X, Loader2, Phone, MessageSquare, Check, CheckCheck, AlertCircle, Users,
} from "lucide-react";
import { toast } from "sonner";
import { walletApi, gbp } from "@/lib/wallet";
import SEO from "@/components/site/SEO";
import CustomerBottomNav from "@/components/customer/CustomerBottomNav";

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}
function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return fmtTime(iso);
  const yest = new Date(today); yest.setDate(yest.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function StatusTick({ status }) {
  if (status === "delivered") return <CheckCheck className="h-3.5 w-3.5 text-sky-500" data-testid="status-delivered" />;
  if (status === "sent" || status === "queued") return <Check className="h-3.5 w-3.5 text-brand-muted" data-testid="status-sent" />;
  if (status === "failed") return <AlertCircle className="h-3.5 w-3.5 text-rose-500" data-testid="status-failed" />;
  return null;
}

/* ────────────────────────── CONVERSATIONS LIST ──────────────────────────── */
export default function CustomerMessages() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await walletApi.get("/customer/sms/conversations");
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load conversations");
      setItems([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((c) =>
      (c.contact_name || "").toLowerCase().includes(needle) ||
      c.phone_e164.includes(needle.replace(/^\+/, ""))
    );
  }, [items, q]);

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink pb-24 md:pb-10" data-testid="customer-messages">
      <SEO title="Messages" path="/app/messages" />
      <header className="bg-white border-b border-brand-line sticky top-0 z-30">
        <div className="container-page flex items-center justify-between h-16">
          <Link to="/app" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/70 hover:text-brand-ink" data-testid="messages-more-link">
            <ArrowLeft className="h-4 w-4" /> More
          </Link>
          <h1 className="font-display font-black text-base">Messages</h1>
          <Link to="/app/messages/new" data-testid="messages-new-btn"
            className="inline-flex items-center gap-1 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-3 py-1.5 text-xs font-bold">
            <Plus className="h-3.5 w-3.5" /> New
          </Link>
        </div>
      </header>

      <div className="container-page py-4 max-w-2xl">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search conversations"
            data-testid="messages-search"
            className="w-full rounded-full bg-white border border-brand-line pl-9 pr-9 py-2.5 text-sm focus:border-brand-green focus:outline-none"
          />
          {q && (
            <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-brand-bgSoft" data-testid="messages-search-clear">
              <X className="h-3.5 w-3.5 text-brand-muted" />
            </button>
          )}
        </div>

        {items === null ? (
          <div className="rounded-2xl border border-brand-line bg-white p-8 text-center text-sm text-brand-muted" data-testid="messages-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-brand-line bg-white p-10 text-center" data-testid="messages-empty">
            <MessageSquare className="h-7 w-7 mx-auto text-brand-muted" />
            <p className="mt-3 font-display font-black text-lg">
              {q ? "No conversations match" : "No conversations yet"}
            </p>
            <p className="mt-1 text-sm text-brand-muted">
              {q ? "Try a different search." : "Send your first SMS to start a conversation."}
            </p>
            {!q && (
              <Link to="/app/messages/new" data-testid="messages-empty-cta"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-4 py-2 text-xs font-bold">
                <Plus className="h-3.5 w-3.5" /> New message
              </Link>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-brand-line rounded-2xl border border-brand-line bg-white overflow-hidden" data-testid="messages-list">
            {filtered.map((c) => (
              <li key={c.phone_e164}>
                <Link
                  to={`/app/messages/${encodeURIComponent(c.phone_e164)}`}
                  data-testid={`conversation-${c.phone_e164}`}
                  className="flex items-center gap-3 p-4 hover:bg-brand-bgSoft"
                >
                  <div className="shrink-0 h-11 w-11 rounded-full bg-brand-greenSoft text-brand-greenDark flex items-center justify-center font-display font-black">
                    {(c.contact_name || c.phone_e164).slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm text-brand-ink truncate">{c.contact_name || c.phone_e164}</p>
                      <span className="text-[10px] text-brand-muted shrink-0">{fmtRelative(c.last_at)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {c.last_direction === "out" && <StatusTick status={c.last_status} />}
                      <p className="text-xs text-brand-muted truncate flex-1">{c.last_body}</p>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      <CustomerBottomNav />
    </div>
  );
}

/* ────────────────────────────── THREAD VIEW ────────────────────────────── */
export function CustomerMessageThread() {
  const { phone } = useParams();
  const decoded = decodeURIComponent(phone || "");
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [balance, setBalance] = useState(null);
  const bottomRef = useRef(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await walletApi.get(`/customer/sms/conversations/${encodeURIComponent(decoded)}`);
      setThread(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load thread");
    }
  }, [decoded]);

  const refreshBalance = useCallback(async () => {
    try { const { data } = await walletApi.get("/customer/wallet/me"); setBalance(data?.balance); } catch { /* */ }
  }, []);

  useEffect(() => { load(); refreshBalance(); }, [load, refreshBalance]);
  // Poll every 3s for delivery-status updates from the mock carrier.
  useEffect(() => {
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [load]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.items?.length]);

  // Live channel + pricing as user types (smart routing: inapp free vs SMS paid).
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await walletApi.get(
          `/customer/sms/channel-lookup?number=${encodeURIComponent(decoded)}&body=${encodeURIComponent(body || " ")}`
        );
        setPricing(data);
      } catch { setPricing(null); }
    }, 200);
    return () => clearTimeout(t);
  }, [decoded, body]);

  async function send() {
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await walletApi.post("/customer/sms/send", { to: decoded, body: body.trim() });
      setBody("");
      load(); refreshBalance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  }

  const segments = pricing?.segments || 1;
  const chars = body.length;
  const segCap = pricing?.encoding === "UCS-2" ? 70 : 160;

  return (
    <div className="min-h-screen bg-brand-bgSoft flex flex-col" data-testid="customer-message-thread">
      <SEO title={thread?.contact_name || decoded} path="/app/messages" />
      <header className="bg-white border-b border-brand-line sticky top-0 z-30">
        <div className="container-page flex items-center gap-3 h-16">
          <Link to="/app/messages" data-testid="thread-back" className="inline-flex items-center text-sm font-semibold text-brand-ink/70 hover:text-brand-ink">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-brand-ink truncate" data-testid="thread-name">
              {thread?.contact_name || decoded}
            </p>
            <p className="text-[11px] text-brand-muted truncate font-mono">{decoded}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              try { sessionStorage.setItem("od_dial_prefill", JSON.stringify({ number: decoded, name: thread?.contact_name || "", ts: Date.now() })); }
              catch (err) { if (process.env.NODE_ENV !== "production") console.warn("dial prefill", err?.message); }
              nav("/app/dial");
            }}
            data-testid="thread-call-btn"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-brand-green hover:bg-brand-greenHover text-white"
            aria-label="Call"
          >
            <Phone className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="thread-messages">
        {thread === null ? (
          <div className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-brand-muted" /></div>
        ) : thread.items.length === 0 ? (
          <p className="text-center text-sm text-brand-muted mt-10">Send your first message below.</p>
        ) : (
          thread.items.map((m) => (
            <div key={m.id} data-testid={`msg-${m.id}`}
              className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                m.direction === "out"
                  ? "ml-auto bg-brand-greenSoft text-brand-ink rounded-br-md"
                  : "mr-auto bg-white border border-brand-line text-brand-ink rounded-bl-md"
              }`}>
              <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
              <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-brand-muted">
                <span>{fmtTime(m.created_at)}</span>
                {m.direction === "out" && <StatusTick status={m.status} />}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="bg-white border-t border-brand-line p-3" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}>
        <div className="flex items-end gap-2 max-w-2xl mx-auto">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 1000))}
            placeholder="Type a message…"
            rows={1}
            data-testid="composer-body"
            className="flex-1 resize-none rounded-2xl bg-brand-bgSoft border border-brand-line px-3 py-2 text-sm focus:border-brand-green focus:outline-none max-h-32"
          />
          <button
            type="button" onClick={send}
            disabled={!body.trim() || sending}
            data-testid="composer-send"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-brand-green hover:bg-brand-greenHover disabled:bg-brand-line text-white"
            aria-label="Send"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        <div className="max-w-2xl mx-auto mt-1.5 flex items-center justify-between text-[10px] text-brand-muted">
          <span data-testid="composer-counter">{chars}/{segCap} · {segments} {segments === 1 ? "segment" : "segments"}</span>
          <span data-testid="composer-cost">
            {pricing?.channel === "inapp" ? (
              <span className="text-emerald-700 font-bold">Free · OneDial</span>
            ) : pricing ? (
              <>{gbp(pricing.cost || pricing.total_cost || 0)} · Wallet {balance != null ? gbp(balance) : "—"}</>
            ) : (
              <>Wallet {balance != null ? gbp(balance) : "—"}</>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── NEW MESSAGE COMPOSER ───────────────────────── */
export function CustomerMessageNew() {
  const [to, setTo] = useState("+");
  const [body, setBody] = useState("");
  const [contacts, setContacts] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [sending, setSending] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    walletApi.get("/customer/contacts").then(({ data }) => setContacts(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!to || to.length < 5) { setPricing(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await walletApi.get(
          `/customer/sms/pricing/lookup?number=${encodeURIComponent(to)}&body=${encodeURIComponent(body || " ")}`
        );
        setPricing(data);
      } catch { setPricing(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [to, body]);

  async function send() {
    if (!to || !body.trim() || sending) return;
    setSending(true);
    try {
      await walletApi.post("/customer/sms/send", { to, body: body.trim() });
      toast.success("Message sent");
      nav(`/app/messages/${encodeURIComponent(pricing?.to_e164 || to)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  }

  return (
    <div className="min-h-screen bg-brand-bgSoft pb-24" data-testid="customer-message-new">
      <SEO title="New message" path="/app/messages/new" />
      <header className="bg-white border-b border-brand-line sticky top-0 z-30">
        <div className="container-page flex items-center justify-between h-16">
          <Link to="/app/messages" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/70 hover:text-brand-ink" data-testid="new-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <h1 className="font-display font-black text-base">New message</h1>
          <span className="w-12" />
        </div>
      </header>

      <div className="container-page py-5 max-w-2xl space-y-4">
        <div>
          <label className="block text-xs font-semibold text-brand-muted mb-1">To</label>
          <input
            value={to}
            onChange={(e) => setTo(e.target.value.replace(/[^\d+]/g, ""))}
            data-testid="new-to-input"
            className="w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm font-mono focus:border-brand-green focus:outline-none"
            placeholder="+2348012345678"
          />
        </div>

        {contacts.length > 0 && (
          <div data-testid="new-contacts">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-muted mb-2 flex items-center gap-1">
              <Users className="h-3 w-3" /> Pick a contact
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {contacts.slice(0, 8).map((c) => (
                <button key={c.id} type="button" onClick={() => setTo(c.phone_e164)}
                  data-testid={`new-contact-${c.id}`}
                  className="shrink-0 rounded-xl border border-brand-line bg-white hover:bg-brand-bgSoft px-3 py-2 text-left min-w-[140px]">
                  <p className="text-xs font-semibold truncate">{c.name}</p>
                  <p className="text-[10px] text-brand-muted font-mono truncate">{c.phone_e164}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-brand-muted mb-1">Message</label>
          <textarea
            value={body} onChange={(e) => setBody(e.target.value.slice(0, 1000))}
            rows={4}
            data-testid="new-body-input"
            className="w-full resize-none rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm focus:border-brand-green focus:outline-none"
            placeholder="Type your message…"
          />
          <div className="mt-1 flex items-center justify-between text-[10px] text-brand-muted">
            <span data-testid="new-counter">{body.length} chars · {pricing?.segments || 1} segment(s)</span>
            <span data-testid="new-cost">
              {pricing ? `${pricing.country_name} · ${gbp(pricing.total_cost)}` : "Enter a destination"}
            </span>
          </div>
        </div>

        <button
          type="button" onClick={send}
          disabled={!to || !body.trim() || sending}
          data-testid="new-send-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover disabled:bg-brand-line text-white py-3 text-sm font-bold"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send message
        </button>
      </div>
    </div>
  );
}
